import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import SingleBtn from 'components/common/elements/SingleBtn';
import ModalWrapper from 'components/common/overlays/ModalWrapper';
import FormDropdown, {
  TDropdownItem,
} from 'components/common/forms/FormDropdown';
import LabelInput from 'components/common/forms/LabelInput';
import styles from 'styles/components/common/overlays/Modal.module.scss';
import { ReactComponent as CloseIcon } from '../../assets/icons/close.svg';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import { RegisterPracticeChartDefaultValues } from 'constants/chart/common/chart';
import { ChartInfo } from 'constants/chart/common/chartInfo';
import {
  CreatePracticeChartFields,
  TCreatePracticeChart,
} from 'types/form/practiceChart';
import { postPracticeChart } from 'apis/chart/studentPracticeChart';
import { TCreatePracticeChartRequest } from 'types/apis/chart/studentPracticeChart';
import useModal from 'hooks/useModal';
import { isTCustomAxiosError } from 'apis/config/axios';

const RegisterPracticeChart = () => {
  const { closeModal } = useModal();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const methods = useForm<TCreatePracticeChart>({
    defaultValues: RegisterPracticeChartDefaultValues,
  });
  const {
    handleSubmit,
    formState: { errors: formErrors },
    control,
  } = methods;

  const chartTypeList: TDropdownItem[] = ChartInfo.map((chartType) => ({
    label: chartType.name,
    value: chartType.type,
  }));

  const chartTypeNum = useWatch({
    name: CreatePracticeChartFields.ChartTypeNum,
    control,
  });

  const chartRoute = ChartInfo.find((chart) => chart.type === chartTypeNum)
    ?.route;

  const { mutate: postPracticeChartMutation } = useMutation(postPracticeChart, {
    onSuccess: (data) => {
      queryClient.invalidateQueries('getPracticeChartList');
      closeModal();
      if (!chartRoute || !data.data) return;
      navigate(`/practice${chartRoute}/${data.data}/register`);
    },
    onError: (error) => {
      if (isTCustomAxiosError(error)) {
        if (error.code === 810) {
          setErrorMsg(error.message);
        }
      }
    },
  });

  const onValid = (data: TCreatePracticeChartRequest) => {
    postPracticeChartMutation(data);
  };

  return (
    <ModalWrapper>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onValid)}>
          <header className={styles.title_wrapper}>
            <h2 className={styles.title}>연습용 차트 선택</h2>
            <button type="button" onClick={closeModal}>
              <CloseIcon alt="close" fill={STYLES_VARIABLES.GRAY_07} />
            </button>
          </header>
          <p className="fs12 font_purple">
            연습용을 선택한 경우, 해당 차트는 실습 평가에 반영되지 않습니다.
          </p>
          <p className="fs14 fw700 mt16">등록할 차트 종류를 선택해 주세요.</p>
          <p
            className="fs12 mt8 mb8"
            style={{ color: STYLES_VARIABLES.GRAY_07 }}
          >
            한 차트 종류당 최대 5번을 입력할 수 있으며, 그 이후에는 입력이
            불가합니다.
          </p>
          <FormDropdown
            name={CreatePracticeChartFields.ChartTypeNum}
            btnStyle={{
              width: '432px',
              height: '48px',
              paddingLeft: '24px',
              background: STYLES_VARIABLES.WHITE,
            }}
            dropdownStyle={{ width: '432px' }}
            selectList={chartTypeList}
            placeholder="차트 종류를 선택해 주세요."
          />
          <LabelInput
            labelText="차트명을 입력해 주세요."
            labelStyle={{ marginTop: '24px' }}
            name={CreatePracticeChartFields.ChartName}
            placeholderText="차트명을 입력해 주세요."
            rules={{
              required: true,
              pattern: {
                value: /^[a-zA-Z0-9가-힣\s]*$/,
                message: '한글, 영문, 숫자만 입력할 수 있습니다.',
              },
              maxLength: {
                value: 20,
                message: '최대 20자까지 입력할 수 있습니다.',
              },
            }}
          />
          <div style={{ height: '32px' }}>
            {formErrors?.chartName && (
              <span className="error_text">{formErrors.chartName.message}</span>
            )}
            {errorMsg && <span className="error_text">{errorMsg}</span>}
          </div>
          <footer className="modal_btn_wrapper">
            <SingleBtn
              btnText="취소"
              onClick={closeModal}
              hasOutline={true}
              btnStyle={{
                width: '100%',
                fontSize: '14px',
              }}
            />
            <SingleBtn
              btnText="확인"
              type="submit"
              isBtnActive={true}
              btnStyle={{
                width: '100%',
                fontSize: '14px',
              }}
            />
          </footer>
        </form>
      </FormProvider>
    </ModalWrapper>
  );
};

export default RegisterPracticeChart;
